//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import * as Immutable from 'immutable';

import LocationActions from 'common/redux/actions/location';
import SkillActions from 'common/redux/actions/skill';
import ToastActions from 'common/redux/actions/toast';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import TeamList from 'common/components/core/common/TeamList';
import SkillsList from 'common/components/core/common/SkillsList';

import LocationGeocodeActions from 'common/redux/actions/locationGeocodeEdit';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import LocationSelectors from 'common/redux/selectors/location';
import SkillSelectors from 'common/redux/selectors/skill';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import {
    getDispatcherVisibilitySettings,
    getEntitySettings
} from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';
import SingleLocationEditContainer from 'common/entity/locationMaster/SingleLocationEditContainer';

import LocationMasterImportContainer from 'common/entity/locationMaster/LocationMasterImportContainer';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import BulkGeocodeEditContainer from 'common/entity/locationMaster/BulkGeocodeEditContainer';
import {
    ConnectedTeamSelectorFilter,
    ConnectedTeamIdSelectorFilter
} from 'common/entity/components/TeamSelectorFilter';
import {
    ConnectedSkillSelectorFilter,
    ConnectedSkillIdSelectorFilter
} from 'common/entity/components/SkillSelectorFilter';
import LocationTypeFilter from 'common/entity/components/LocationTypeFilter';
import GeocodeConfidenceFilter from 'common/entity/components/GeocodeConfidenceFilter';
import GeocodeSourceFilter from 'common/entity/components/GeocodeSourceFilter';
import SlotFilter from 'common/entity/components/SlotFilter';
import VerificationStatusFilter from 'common/entity/components/VerificationStatusFilter';
import GeneralFilter from 'common/entity/components/GeneralFilter';

import TimeSlotsSelectorModalContainer from 'common/entity/components/multiDaySlot/TimeSlotsSelectorModalContainer';
import LocationSlotsSummary from 'common/entity/components/LocationSlotSummary';

import { ErrorIcon } from 'common/components/core/icon';

import { Modal } from 'common/entity/generic/SingleEntityEditView';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';

import { dashboardColors } from 'common/enums/ColorEnum';

import theme from 'common/entity/BulkEditContainer.scss';
import specificTheme from './LocationMasterContainer.scss';

import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

import PromiseUtils, { PromiseStatus } from 'common/utils/PromiseUtils';
import VisitUtils from 'common/utils/VisitUtils';

import { Loader } from 'common/components/core/loader';
import { Button, ButtonTypes } from 'common/components/core/button';
import VerifyButton from 'common/entity/generic/VerifyButton';
import BulkEditConfirmationDialog from 'common/entity/generic/BulkEditConfirmationDialog';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { PopoverPlaceEnum } from 'common/components/core/popover/popOverFactory';
import { ProviderEnum } from 'common/enums/GeometryEnum';

import { RoleOperationEnum } from 'common/modules/rbac';

import {
    MasterLocation,
    VerificationStatus,
    GeocodingSourceType
} from 'common/models/internal/MasterLocation';
import {
    FilterBuilder,
    FilterOperation,
    LogicalOperation
} from 'common/models/internal/Filter';

import type { Skill } from 'common/models/internal/Skill';
import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { Team } from 'common/models/internal/Team';
import type { Map } from 'immutable';
import type { EntitySettings } from 'common/models/internal/EntitySettings';
import type { ToastType } from 'common/components/core/toast/Toast';
import type { TFunction } from 'react-i18next';
import LearntAuditTaskList from './LearntAuditTaskList';
import { tableButtonWithPopoverFactory } from 'common/components/common/TableButtonWithPopover';

import { memoizedImmutable } from 'common/utils/Memoizer';
import { LocationWithMetadata } from 'common/models/flow/LocationWithMetadata';
import { MultiDaySlot } from 'common/models/internal/MultiDaySlot';
import { HomebaseId } from 'common/models/internal/HomebaseId';
import locationIds from 'common/tests/masterManagement/locationMaster.json';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/locationMaster';

const selectTeamId = ({ teamId }) => teamId;

const selectSkillsId = ({ id }) => id;

const getTeamIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const teamValueFilter = values.map(team => ({
        clientId,
        teamId: team
    }));

    return new FilterBuilder()
        .withName('teams')
        .withOperation(FilterOperation.Contains)
        .withValues(teamValueFilter)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const getSkillIdFilter = ({ clientId, filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    const skillFilterValues = values.map(skill => ({
        clientId,
        id: skill
    }));
    return new FilterBuilder()
        .withName('skills')
        .withOperation(FilterOperation.Contains)
        .withValues(skillFilterValues)
        .withAllowEmptyOrNull(allowEmptyOrNull);
};

const VerifiedButton = ({ location, testId }) => {
    const isVerified = location.isVerified();
    return (
        <VerifyButton
            nonEditable
            theme={theme}
            isVerified={isVerified}
            testId={testId}
        />
    );
};

type TdPropsType = {
    rest: {
        t: TFunction,
        theme: Object
    }
};

const TaskListButtonWithPopover = tableButtonWithPopoverFactory({
    ListElement: LearntAuditTaskList
});

const COLUMNS = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick, theme }
                }
            } = props;
            return (
                <Checkbox
                    checkboxType={CheckboxTypes.Solid}
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    theme={theme}
                    className={theme.selectionCell}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'locationTable.id',
        accessor: 'id',
        frozen: true,
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'locationTable.code',
        id: 'code',
        accessor: 'code',
        frozen: true,
        minWidth: 100,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'locationTable.type',
        id: 'locationType',
        accessor: 'type',
        Cell: props => {
            const {
                value,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const cellText = t(`masterLocationProperties.types.${value}`);
            return (
                <div title={cellText} className={theme.cellText}>
                    {cellText}
                </div>
            );
        },
        FilterComponent: LocationTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        },
        property: 'type',
        minWidth: 100
    },
    {
        headerTextId: 'locationTable.name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'locationTable.teamName',
        id: 'teams.name',
        exportId: 'teams.name',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return <TeamList teams={teams} teamIds={teamIds} theme={theme} />;
        }
    },
    {
        headerTextId: 'locationTable.teamId',
        id: 'teams.id',
        exportId: 'teams.id',
        accessor: 'teams',
        sortable: false,
        minWidth: 200,
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { theme, teams }
                }
            } = props;
            const teamIds = original.teams;
            return (
                <TeamList
                    teams={teams}
                    teamIds={teamIds}
                    theme={theme}
                    selectTeamName={selectTeamId}
                />
            );
        }
    },
    {
        headerTextId: 'locationTable.address.placeName',
        accessor: 'address.placeName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'locationTable.address.address',
        accessor: 'address.formattedAddress',
        minWidth: 300,
        infixSearchable: true
    },
    {
        headerTextId: 'locationTable.address.locality',
        accessor: 'address.localityName',
        minWidth: 150
    },
    {
        headerTextId: 'locationTable.address.pincode',
        accessor: 'address.pincode',
        minWidth: 100
    },
    {
        headerTextId: 'locationTable.address.city',
        accessor: 'address.city',
        minWidth: 100
    },
    {
        headerTextId: 'locationTable.address.state',
        accessor: 'address.state',
        minWidth: 100
    },
    {
        headerTextId: 'locationTable.address.country',
        accessor: 'address.countryCode',
        minWidth: 50
    },
    {
        headerTextId: 'locationTable.latitude',
        exportId: 'latitude',
        accessor: 'latLng.lat',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'locationTable.longitude',
        exportId: 'longitude',
        accessor: 'latLng.lng',
        sortable: false,
        filterable: false,
        minWidth: 150
    },
    {
        headerTextId: 'locationTable.confidence',
        id: 'confidence',
        accessor: 'geocodingMetadata.confidence',
        filterable: true,
        sortable: true,
        minWidth: 100,
        FilterComponent: GeocodeConfidenceFilter,
        Cell: props => {
            const {
                original: data,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const confidenceLevel = data.geocodingMetadata?.confidence;
            if (!confidenceLevel) {
                return null;
            }
            return t(
                `masterLocationProperties.confidenceLevels.${confidenceLevel}`
            );
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('geocodingMetadata.confidence')
                .withOperation(FilterOperation.Equals)
                .withValues(values);
        }
    },
    {
        headerTextId: 'locationTable.geocodingSource',
        id: 'geoSource',
        fieldId: 'geocodingMetadata.provider',
        property: 'geocodingMetadata.provider',
        Cell: props => {
            const {
                original: data,
                tdProps: {
                    rest: { t }
                }
            } = props;
            const { provider } = data.geocodingMetadata || {};
            if (!provider) {
                return null;
            }
            return t(
                `masterLocationProperties.geocodingSourcesTypes.${provider}`
            );
        },
        filterable: true,
        sortable: true,
        minWidth: 100,
        FilterComponent: GeocodeSourceFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values }
            } = filteredColumn;
            const sourceValues = [];
            if (values.includes(GeocodingSourceType.Client)) {
                sourceValues.push(
                    ...[ProviderEnum.ClientRead, ProviderEnum.ClientGeocoder]
                );
            }
            if (values.includes(GeocodingSourceType.Locus)) {
                sourceValues.push(ProviderEnum.LocusGeocoder);
            }
            if (values.includes(GeocodingSourceType.SystemLearnt)) {
                sourceValues.push(ProviderEnum.SystemLearnt);
            }
            return new FilterBuilder()
                .withName('geocodingMetadata.provider')
                .withOperation(FilterOperation.Equals)
                .withValues(sourceValues)
                .withLogicalOperation(LogicalOperation.OR);
        }
    },
    {
        headerTextId: 'locationTable.verified',
        id: 'verified',
        accessor: 'geocodingMetadata.verified',
        FilterComponent: VerificationStatusFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const verificationValues = [];
            if (
                filteredColumn.value.values.includes(
                    VerificationStatus.Verified
                )
            ) {
                verificationValues.push('true');
            }
            if (
                filteredColumn.value.values.includes(
                    VerificationStatus.Unverified
                )
            ) {
                verificationValues.push('false');
            }
            return new FilterBuilder()
                .withName('geocodingMetadata.verified')
                .withOperation(FilterOperation.Equals)
                .withValues(verificationValues)
                .withLogicalOperation(LogicalOperation.OR);
        },
        Cell: props => {
            const {
                original: location,
                tdProps: {
                    rest: { theme, toggleVerification }
                },
                row
            } = props;
            return (
                <VerifiedButton
                    theme={theme}
                    location={location}
                    toggleVerification={toggleVerification}
                    testId={`${locationIds.table.verifiedButton}-${row._index}`}
                />
            );
        },
        minWidth: 100
    },
    {
        headerTextId: 'locationTable.learntFrom',
        accessor: 'locationLearningAudit',
        filterable: false,
        sortable: false,
        Cell: (props: { original: MasterLocation, tdProps: TdPropsType }) => {
            const {
                original,
                tdProps: {
                    rest: { t, theme }
                }
            } = props;
            const { locationLearningAudit } = original;
            if (locationLearningAudit.size === 0) {
                return '-';
            }

            const { sources } = locationLearningAudit.last();
            return (
                <TaskListButtonWithPopover
                    ripple={false}
                    popOverClassName={theme.taskListPopover}
                    buttonTooltip={t('liveView:clickForMoreDetails')}
                    list={sources}
                    listTitle={t('locationTable.task_plural')}
                    theme={theme}
                    defaultDataGrouped
                    className={classnames(theme.tableButton, theme.plain)}
                    preferPlace={PopoverPlaceEnum.Left}
                    t={t}
                >
                    {t('locationTable.viewTasks', {
                        count: sources.size
                    })}
                </TaskListButtonWithPopover>
            );
        }
    },
    {
        headerTextId: 'locationTable.slots',
        id: 'slots',
        fieldId: 'timeSlots',
        property: 'timeSlots',
        sortable: false,
        minWidth: 150,
        FilterComponent: SlotFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { values, allowEmptyOrNull }
            } = filteredColumn;
            const slots = values.map(slot => ({
                dayOfWeek: slot
            }));
            return new FilterBuilder()
                .withName('timeSlots')
                .withOperation(FilterOperation.Contains)
                .withValues(slots)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        accessor: (data: MasterLocation) => {
            const { timeSlots } = data.sortTimeSlots();
            const DaysOfWeekActive = [];
            timeSlots.forEach(timeSlot => {
                if (timeSlot.slots.size) {
                    DaysOfWeekActive.push(timeSlot.getShortCode());
                }
            });
            return DaysOfWeekActive.join(' ');
        },
        Cell: props => {
            const { original: location, row } = props;
            const { t } = props.column.getHeaderProps();
            const { timeSlots } = location.initializeWeekSlots();
            return (
                <LocationSlotsSummary
                    timeSlots={timeSlots}
                    title={t('workingHours')}
                    testId={`${locationIds.table.locationSlots}-${row._index}`}
                />
            );
        }
    },
    {
        headerTextId: 'locationTable.multiDaySlotSpan',
        accessor: 'multiDaySlotSpan',
        id: 'multiDaySlotSpan'
    },
    {
        headerTextId: 'locationTable.tat',
        id: 'transactionDuration',
        property: 'transactionDuration',
        filterable: false,
        dataType: CellDataTypes.HumanizedTime,
        accessor: 'transactionDuration',
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'locationTable.contactId',
        accessor: 'contact.id',
        fieldId: 'contact',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'locationTable.contactName',
        accessor: 'contact.name',
        fieldId: 'contact',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'locationTable.contactNumber',
        accessor: 'contact.phoneNumber.phoneNumber',
        fieldId: 'contactNumber',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'locationTable.skillId',
        sortable: false,
        id: 'skills.id',
        exportId: 'skills.id',
        accessor: 'skills',
        FilterComponent: ConnectedSkillIdSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = (original: MasterLocation).skills;
            return (
                <SkillsList
                    skills={skills}
                    skillIds={skillIds}
                    labelSelector={selectSkillsId}
                />
            );
        }
    },
    {
        headerTextId: 'locationTable.skillName',
        sortable: false,
        id: 'skills.name',
        exportId: 'skills.name',
        accessor: 'skills',
        FilterComponent: ConnectedSkillSelectorFilter,
        getCustomFilterInfo: getSkillIdFilter,
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { skills }
                }
            } = props;
            const skillIds = (original: MasterLocation).skills;
            return <SkillsList skills={skills} skillIds={skillIds} />;
        }
    },
    {
        headerTextId: 'locationTable.verifiedBy',
        id: 'verifiedBy',
        sortable: false,
        filterable: false,
        fieldId: 'geocodingMetadata.verifiedBy',
        Cell: props => {
            const {
                original: location,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const verifiedBy = VisitUtils.formatIDs(
                location?.geocodingMetadata?.verifiedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={verifiedBy} className={theme.cellText}>
                    {verifiedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'locationTable.verifiedAt',
        accessor: 'geocodingMetadata.verifiedAt',
        id: 'verifiedAt',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'locationTable.crossDockServiceable',
        accessor: 'crossDockInfo.serviceable',
        id: 'xdockServiceable',
        isHidden: ({ isFieldHidden }) => isFieldHidden('xdockServiceable'),
        sortable: true,
        filterable: true,
        Cell: props => {
            const {
                value: xdockServiceable,
                tdProps: {
                    rest: { t }
                }
            } = props;
            return xdockServiceable ? t('boolean.yes') : t('boolean.no');
        },
        FilterComponent: GeneralFilter,
        getFilterOptions: ({ column }) => {
            const { t } = column.getHeaderProps();
            const optionsForSelect = [
                { value: true, label: t('boolean.yes') },
                { value: false, label: t('boolean.no') }
            ];
            return optionsForSelect;
        }
    },
    {
        headerTextId: 'locationTable.priority',
        accessor: 'priority',
        id: 'priority'
    },
    {
        headerTextId: 'locationTable.crossDockIds',
        accessor: 'crossDockInfo.crossDockIds',
        exportId: 'crossDockInfo.crossDockIds.homebaseId',
        id: 'crossDockId',
        isHidden: ({ isFieldHidden }) => isFieldHidden('crossDockId'),
        Cell: (props: { value: Immutable.List<HomebaseId> }) => {
            const { value: crossDockIds } = props;
            return crossDockIds
                .map(crossDockId => {
                    return crossDockId.homebaseId;
                })
                .join(',');
        }
    },
    {
        headerTextId: 'locationTable.vehicleModelId',
        accessor: 'vehicleModelId.modelId',
        id: 'vehicleModelId',
        isHidden: ({ isFieldHidden }) => isFieldHidden('vehicleModelId')
    },
    {
        headerTextId: 'locationTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: location,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                location?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'locationTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'locationTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: location,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                location?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'locationTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

const BulkEditErrorsModal = ({ t, theme, errors, onClose }) => {
    return (
        <div className={theme.bulkEditErrorsModal}>
            <span className={theme.title}>
                {t('bulkEdit.updatesFailed', { count: errors.length })}
            </span>
            <Button
                buttonType={ButtonTypes.Icon}
                circle
                icon="cancel"
                className={classnames(theme.button, theme.closeIcon)}
                theme={theme}
                onClick={onClose}
            />
            <div className={theme.errorsList}>
                {errors.map((error, index) => {
                    return (
                        <span key={index} className={theme.error}>
                            <ErrorIcon visible theme={theme} /> {error}
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

const handlePollingRequired = entities => {
    const { locations } = entities;
    const pendingGeocoded =
        locations.length &&
        locations.filter(location => location.geocoded === false);
    return !!(pendingGeocoded && pendingGeocoded.length);
};

type PaginationInfoDataType = {
    currentPage: number,
    numberOfPages: number,
    total: number
};

type LocationsDataType = {
    locations: Array<MasterLocation>,
    paginationInfo: PaginationInfoDataType
};

type Props = {
    clientId: string,
    teams: Map<string, Team>,
    skills: Array<Skill>,
    fetchEntities: ({ clientId: string }, Object) => void,
    pollFetchEntities: ({ clientId: string }, Object) => void,
    pollLocationSearch: Function,
    geocodeLocationsAction: Function,
    geocodeLocationsSuccessAction: Function,
    fetchSkills: ({ clientId: string }) => void,
    disableEntity: ({ clientId: string, locationId: string }) => Promise<*>,
    toggleVerification: (
        { clientId: string, locationId: string },
        MasterLocation,
        options?: Object
    ) => Promise<*>,
    clearData: () => void,
    selectedRows: Array<string>,
    data: ?LocationsDataType,
    isAddLocationActive: boolean,
    entityIdToEdit: ?string,
    isBulkEditLocationActive: boolean,
    isLoading: boolean,
    isPolling: boolean,
    location: Location,
    history: RouterHistory,
    theme: Object,
    t: (string, ?Object) => string,
    entitySettings: ?EntitySettings,
    tableSettings: ?Array<Object>,
    updateLocation: (
        {
            clientId: string,
            locationId: string
        },
        MasterLocation,
        MasterLocation,
        options?: Object
    ) => Promise<*>,
    showSuccessToast: ($Shape<ToastType>) => void,
    showErrorToast: ($Shape<ToastType>) => void,
    saveTableSettings: (string, Array<Object>) => void,
    toggleRowSelection: string => void,
    latLngActions: Object,
    verifyEntityAction: Function,
    editEntityLatLngAction: Function,
    validateGeocodesAction: Function,
    clearGeocodeValidationDataAction: Function,
    isCrossDockClient: boolean,
    isTaskSplitClient: boolean
};

type State = {
    showSlotSelector: boolean,
    showBulkEditProgress: boolean,
    showBulkEditErrors: boolean,
    bulkEditErrors: Array<string>,
    geocodingEditActive?: boolean,
    needsBulkEditConfirmation?: boolean,
    bulkEditAcceptCallback?: ?(any) => void,
    prevFetchEntitiesParams: Object
};

const LocationMaster = entityMasterFactory({
    EntityEditViewContainer: SingleLocationEditContainer,
    EntityImportContainer: LocationMasterImportContainer,
    EntityExportContainer: MasterExportContainer,
    COLUMNS,
    options: {
        entityTableId: 'LocationMaster',
        operations: {
            edit: RoleOperationEnum.UPDATE_LOCATIONS,
            add: RoleOperationEnum.UPDATE_LOCATIONS,
            export: RoleOperationEnum.EXPORT_LOCATIONS
        }
    }
});

class LocationMasterContainer extends React.PureComponent<Props, State> {
    prevRequestParams: Object;
    columns: Array<Object>;
    masterContainer: ?React.ElementRef<any>;
    stopEntityPollingHandler: any;

    constructor(props) {
        super(props);
        this.state = {
            showSlotSelector: false,
            showBulkEditProgress: false,
            showBulkEditErrors: false,
            bulkEditErrors: [],
            prevFetchEntitiesParams: {}
        };
    }

    componentDidMount = () => {
        this.fetchOtherResources();
    };

    componentDidUpdate = (prevProps: Props) => {
        const { clientId } = this.props;
        if (clientId !== prevProps.clientId) {
            this.fetchOtherResources();
        }
    };

    componentWillUnmount() {
        this.stopEntityPollingHandler && this.stopEntityPollingHandler();
    }

    getExtraColumnProperties = () => {
        const { t } = this.props;
        return [
            {
                id: 'slots',
                visible: true,
                menu: [
                    {
                        id: 'change-slots',
                        label: t('locationTable.changeSlots'),
                        onClick: this.toggleSlotSelectorVisiblity,
                        isDisabled: this.isBulkSlotsChangeDisabled
                    }
                ]
            },
            ...this.getCustomColumnProperties()
        ];
    };

    getCustomColumnProperties() {
        return CustomColumnHelpers.makeCustomColumnProperties(
            this.props.entitySettings,
            'locationSettings',
            this.onCustomColumnOptionClick,
            this.isCustomColumnOptionDisabled
        );
    }

    isCustomColumnOptionDisabled = () => {
        return !this.props.selectedRows.length;
    };

    onCustomColumnOptionClick = (
        customColumnName: string,
        optionValue: string
    ) => {
        this.setState({
            needsBulkEditConfirmation: true,
            bulkEditAcceptCallback: () => {
                this.showBulkEditProgress();
                this.applyCustomColumnBulkEdit(customColumnName, optionValue);
            }
        });
    };

    applyCustomColumnBulkEdit = (
        customColumnName: string,
        optionValue: string
    ) => {
        const selectedLocations = this.getAllSelectedLocations();
        const promisesToProcess = {};
        selectedLocations.forEach(location => {
            const { customProperties = Immutable.Map() } = location;
            const updatedLocation = location.setCustomProperties(
                customProperties.set(customColumnName, optionValue)
            );
            promisesToProcess[location.id] = this.props.updateLocation(
                {
                    clientId: updatedLocation.clientId,
                    locationId: updatedLocation.id
                },
                location,
                updatedLocation,
                { dontInjectError: true, suppressToasts: true }
            );
        });
        this.processBulkEditPromises(promisesToProcess, {
            onRetry: this.applyCustomColumnBulkEdit.bind(
                this,
                customColumnName,
                optionValue
            )
        });
    };

    handleBulkEditApproval = () => {
        const { bulkEditAcceptCallback } = this.state;
        bulkEditAcceptCallback && bulkEditAcceptCallback();
        this.handleBulkEditDismiss();
    };

    handleBulkEditDismiss = () => {
        this.setState({
            needsBulkEditConfirmation: false,
            bulkEditAcceptCallback: null
        });
    };

    fetchOtherResources = () => {
        const { clientId, fetchSkills } = this.props;
        fetchSkills({ clientId });
    };

    showBulkEditProgress = () => {
        this.setState({
            showBulkEditProgress: true
        });
    };

    hideBulkEditProgress = () => {
        this.setState({ showBulkEditProgress: false });
    };

    showBulkEditErrors = () => {
        this.setState({
            showBulkEditErrors: true
        });
    };

    hideBulkEditErrors = () => {
        this.setState({
            showBulkEditErrors: false
        });
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { original } = rowInfo;
        const { id } = original;
        const {
            teams,
            skills,
            toggleVerification,
            selectedRows,
            toggleRowSelection,
            t,
            theme,
            isGlobalPersonnel
        } = this.props;
        const isChecked = selectedRows.indexOf(id) >= 0;
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            isChecked,
            className: classnames({ [theme.selectedRow]: isChecked }),
            theme,
            t,
            teams,
            skills,
            toggleVerification,
            isGlobalPersonnel
        };
    };

    handleBulkLocationGeocodeDismiss = (changed: boolean = false) => {
        this.setState({ geocodingEditActive: false });
        if (changed) {
            this.masterContainer && this.masterContainer.reloadData();
        }
        this.triggerPollEntityForPrevRequest();
    };

    handleGeocodingEditClick = () => {
        this.stopEntityPollingHandler && this.stopEntityPollingHandler();
        this.setState({ geocodingEditActive: true });
    };

    triggerPollEntityForPrevRequest = () => {
        const { fetchEntities } = this.props;
        const { params, body, options } = this.state.prevFetchEntitiesParams;
        if (params && body) {
            fetchEntities(params, body, { ...options, isPolling: true });
        }
    };

    toggleSlotSelectorVisiblity = () => {
        const { selectedRows } = this.props;
        const { showSlotSelector } = this.state;
        if (!showSlotSelector) {
            if (!selectedRows.length) {
                return;
            }
            this.setState({ showSlotSelector: true });
            return;
        }
        this.setState({ showSlotSelector: false });
    };

    getAllSelectedLocations = () => {
        const { data, selectedRows } = this.props;
        if (!data) {
            throw new Error("can't find locationsData");
        }
        return data.entities.filter(location => {
            return selectedRows.indexOf(location.id) > -1;
        });
    };

    isMarkVerifiedDisabled = () => {
        const { data, selectedRows } = this.props;
        if (!data || selectedRows.length === 0) {
            return true;
        }
        return this.getAllSelectedLocations().every(location =>
            location.isVerified()
        );
    };

    isMarkUnverifiedDisabled = () => {
        const { data, selectedRows } = this.props;
        if (!data || selectedRows.length === 0) {
            return true;
        }
        return this.getAllSelectedLocations().every(
            location => !location.isVerified()
        );
    };

    isBulkSlotsChangeDisabled = () => {
        return this.props.selectedRows.length === 0;
    };

    handleSlotsChange = (slots: Array<MultiDaySlot>) => {
        this.showBulkEditProgress();
        this.setSlotsForLocations(slots, this.props.selectedRows);
    };

    setSlotsForLocations = (slots: Array<MultiDaySlot>, ids: Array<string>) => {
        const { data } = this.props;
        if (!data) {
            throw new Error("can't find locationsData");
        }
        const selectedLocations = data.entities.filter(location => {
            return ids.indexOf(location.id) > -1;
        });
        const promisesToProcess = {};
        selectedLocations.forEach(location => {
            const updatedLocation = location.setTimeSlots(
                Immutable.List(slots)
            );
            promisesToProcess[location.id] = this.props.updateLocation(
                {
                    clientId: updatedLocation.clientId,
                    locationId: updatedLocation.id
                },
                location,
                updatedLocation,
                { dontInjectError: true, suppressToasts: true }
            );
        });
        this.processBulkEditPromises(promisesToProcess, {
            onRetry: this.setSlotsForLocations.bind(this, slots)
        });
    };

    processBulkEditPromises = (
        promisesToProcess: { [key: string]: Promise<*> },
        options: {
            onRetry: (Array<string>) => void
        }
    ) => {
        const { showSuccessToast, showErrorToast, t } = this.props;
        const { onRetry } = options;
        this.setState({
            bulkEditErrors: [],
            showBulkEditErrors: false
        });
        PromiseUtils.processAll(promisesToProcess).then(results => {
            const rejectedPromiseIds: Array<string> = [];
            results.forEach(result => {
                if (result.status === PromiseStatus.REJECTED && result.id) {
                    rejectedPromiseIds.push(result.id);
                    this.setState(state => ({
                        bulkEditErrors: [
                            ...state.bulkEditErrors,
                            result.error.message
                        ]
                    }));
                }
            });
            if (rejectedPromiseIds.length !== results.length) {
                showSuccessToast({
                    message: t('locationTable.bulkEditSuccessToast', {
                        count: results.length - rejectedPromiseIds.length
                    }),
                    timeout: 5000
                });
            }
            if (rejectedPromiseIds.length === 0) {
                // Stop Loading
                this.hideBulkEditProgress();
                return;
            }

            const errorContent = (
                <div className={theme.errorToastContentWrap}>
                    <span className={theme.message}>
                        {t('bulkEdit.updatesFailed', {
                            count: rejectedPromiseIds.length
                        })}
                    </span>
                    <span
                        className={theme.viewErrors}
                        onClick={this.showBulkEditErrors}
                    >
                        {t('bulkEdit.viewErrors')}
                    </span>
                </div>
            );

            const actions = ({ hideToast }) => {
                const tryAgain = () => {
                    onRetry(rejectedPromiseIds);
                    hideToast();
                };

                const cancel = () => {
                    hideToast();
                    this.hideBulkEditProgress();
                };
                return (
                    <div className={theme.errorToastButtons}>
                        <Button
                            flat
                            semibold
                            uppercase
                            label={t('bulkEdit.tryAgain')}
                            color={dashboardColors.salmonTwo.name}
                            theme={theme}
                            buttonType={ButtonTypes.Text}
                            onClick={tryAgain} //eslint-disable-line react/jsx-no-bind
                        />
                        <Button
                            buttonType={ButtonTypes.Icon}
                            circle
                            icon="cancel"
                            className={classnames(
                                theme.button,
                                theme.closeIcon
                            )}
                            theme={theme}
                            onClick={cancel} //eslint-disable-line react/jsx-no-bind
                        />
                    </div>
                );
            };
            showErrorToast({
                customContent: errorContent,
                customAction: actions,
                timeout: 0
            });
        });
    };

    renderBulkProgress() {
        const { theme, t } = this.props;
        const { bulkEditErrors } = this.state;
        return (
            <div className={theme.bulkUpdatesLoader}>
                {bulkEditErrors.length ? (
                    <div className={theme.failedErrorWrap}>
                        <span>{t('bulkEdit.failed')}</span>
                    </div>
                ) : (
                    <Loader
                        theme={theme}
                        active
                        withText
                        title={t('locationTable.updating')}
                    />
                )}
            </div>
        );
    }

    getAdditionalTableProps = () => {
        const { teams } = this.props;
        return {
            teams
        };
    };

    getMasterContainerRef = ref => {
        this.masterContainer = ref;
    };

    @memoizedImmutable()
    getLocationsToGeocode(selectedRows, locations) {
        const locationMap = locations.reduce((res, locationEntity) => {
            if (selectedRows.includes(locationEntity.id)) {
                const updLocation = LocationWithMetadata.fromJS({});
                return res.set(
                    locationEntity.id,
                    updLocation.setLocation(locationEntity)
                );
            }
            return res;
        }, Immutable.Map());
        return locationMap.valueSeq();
    }

    fetchEntities = (params, body, options) => {
        const { fetchEntities } = this.props;
        this.stopEntityPollingHandler && this.stopEntityPollingHandler();
        this.setState({
            prevFetchEntitiesParams: {
                params,
                body,
                options
            }
        });
        fetchEntities(params, body, options);
    };

    onFetchEntityPollingSuccess = entities => {
        if (entities && !handlePollingRequired(entities)) {
            this.stopEntityPollingHandler && this.stopEntityPollingHandler();
        }
    };

    onFetchEntityPollingFailure = () => {
        this.stopEntityPollingHandler && this.stopEntityPollingHandler();
    };

    fetchEntityPollingHandler = (
        entities: Object = {},
        options: Object = {}
    ) => {
        const { params, body } = options;
        const { pollFetchEntities } = this.props;
        if (handlePollingRequired(entities)) {
            this.stopEntityPollingHandler && this.stopEntityPollingHandler();
            this.stopEntityPollingHandler = pollFetchEntities(params, body, {
                onSuccessCallback: this.onFetchEntityPollingSuccess,
                onFailureCallback: this.onFetchEntityPollingFailure
            });
        }
    };

    getIsRowLoading = (original, dataType) => {
        if (dataType === CellDataTypes.LatLng) {
            return original.get('geocoded') === false;
        }
    };

    isFieldHidden = column => {
        if (column === 'crossDockId' || column === 'xdockServiceable') {
            return !this.props.isCrossDockClient;
        }
        if (column === 'vehicleModelId') {
            return !this.props.isTaskSplitClient;
        }
    };

    render() {
        const {
            t,
            selectedRows,
            geocodeLocationsAction,
            geocodeLocationsSuccessAction,
            pollLocationSearch,
            clientId,
            data,
            theme,
            verifyEntityAction,
            editEntityLatLngAction,
            validateGeocodesAction,
            clearGeocodeValidationDataAction
        } = this.props;
        const {
            showSlotSelector,
            showBulkEditProgress,
            showBulkEditErrors,
            bulkEditErrors,
            geocodingEditActive,
            needsBulkEditConfirmation
        } = this.state;
        let enableGeocodingEdit = false;
        let geocodeEditLocations = Immutable.Map();
        if (selectedRows.length > 0) {
            enableGeocodingEdit = true;
            if (data) {
                geocodeEditLocations = this.getLocationsToGeocode(
                    selectedRows,
                    data.entities
                );
            }
        }
        const customActions = {
            onLocationEdit: enableGeocodingEdit
                ? this.handleGeocodingEditClick
                : null
        };
        return (
            <div className={theme.wrap}>
                {showBulkEditProgress ? this.renderBulkProgress() : null}
                <LocationMaster
                    {...this.props}
                    getIsRowLoading={this.getIsRowLoading}
                    fetchEntities={this.fetchEntities}
                    refProvider={this.getMasterContainerRef}
                    customActions={customActions}
                    emptyStateImageSrc="/img/location-master-empty.png"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId={'locationId'}
                    entitySettingsName={'locationSettings'}
                    fetchOptions={{ clientId }}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    getExtraColumnProperties={this.getExtraColumnProperties}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    fetchEntityPollingHandler={this.fetchEntityPollingHandler}
                    isFieldHidden={this.isFieldHidden}
                >
                    <Modal theme={theme}>
                        {showBulkEditErrors ? (
                            <BulkEditErrorsModal
                                theme={theme}
                                t={t}
                                errors={bulkEditErrors}
                                onClose={this.hideBulkEditErrors}
                            />
                        ) : null}
                    </Modal>
                    {showSlotSelector ? (
                        <TimeSlotsSelectorModalContainer
                            className={theme.slotSelectorModal}
                            theme={theme}
                            defaultSlots={this.getAllSelectedLocations()[0].timeSlots.toArray()}
                            onDismiss={this.toggleSlotSelectorVisiblity}
                            onSlotApply={this.handleSlotsChange}
                        />
                    ) : null}
                    {geocodingEditActive ? (
                        <BulkGeocodeEditContainer
                            entity={ENTITY_TABLE_ID}
                            verifyEntityAction={verifyEntityAction}
                            editEntityLatLngAction={editEntityLatLngAction}
                            validateGeocodesAction={validateGeocodesAction}
                            clearGeocodeValidationDataAction={
                                clearGeocodeValidationDataAction
                            }
                            geocodeEditLocations={geocodeEditLocations}
                            geocodeLocationsAction={geocodeLocationsAction}
                            geocodeLocationsSuccessAction={
                                geocodeLocationsSuccessAction
                            }
                            pollLocationSearch={pollLocationSearch}
                            onDismiss={this.handleBulkLocationGeocodeDismiss}
                        />
                    ) : (
                        []
                    )}
                    {needsBulkEditConfirmation ? (
                        <BulkEditConfirmationDialog
                            selectionCount={selectedRows.length}
                            entityI18nId={'entities:masterLocation'}
                            onAccept={this.handleBulkEditApproval}
                            onCancel={this.handleBulkEditDismiss}
                        />
                    ) : (
                        []
                    )}
                </LocationMaster>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = LocationSelectors.isEntitiesLoading(state, ownProps);
    const isPolling = LocationSelectors.isLocationPolling(state, ownProps);
    const data = LocationSelectors.getEntities(state, ownProps);
    const skills = SkillSelectors.getEntities(state, ownProps);
    const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
    const isImportEntityActive = isImportEntityActiveSelector(state, ownProps);
    const isExportEntityActive = isExportEntityActiveSelector(state, ownProps);
    const entityIdToEdit = editEntityIdSelector(state, ownProps);
    const teams = teamsSelector(state, ownProps);
    const entitySettings = getEntitySettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
    const dispatcherVisibilitySettings = getDispatcherVisibilitySettings(
        state,
        ownProps
    );
    const isCrossDockClient = dispatcherVisibilitySettings?.showCrossDockFields;
    const isTaskSplitClient =
        dispatcherVisibilitySettings?.showTaskSplitFieldsInLocation;
    return {
        clientId,
        teams,
        skills,
        isLoading,
        isPolling,
        data,
        isAddEntityActive,
        entityIdToEdit,
        entitySettings,
        tableSettings,
        isImportEntityActive,
        isExportEntityActive,
        isGlobalPersonnel,
        isCrossDockClient,
        isTaskSplitClient
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: LocationActions.fetchActiveEntities,
    pollFetchEntities: LocationActions.pollFetchEntities,
    pollLocationSearch: LocationActions.pollLocationSearch,
    geocodeLocationsAction: LocationActions.bulkGeocode,
    bulkDisableEntity: LocationActions.bulkDisableEntity,
    geocodeLocationsSuccessAction: LocationActions.bulkGeocodeSuccess,
    fetchSkills: SkillActions.fetchEntities,
    updateLocation: LocationActions.patchEntity,
    toggleVerification: LocationActions.toggleLocationVerification,
    disableEntity: LocationActions.disableEntity,
    clearData: LocationActions.clearData,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    showSuccessToast: ToastActions.showSuccessToast,
    showErrorToast: ToastActions.showErrorToast,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    verifyEntityAction: LocationGeocodeActions.verifyEntity,
    editEntityLatLngAction: LocationGeocodeActions.editEntityLatLng,
    validateGeocodesAction: LocationGeocodeActions.validateGeocodes,
    clearGeocodeValidationDataAction:
        LocationGeocodeActions.clearGeocodeValidationData
});

const enhance = compose(
    themr(null, theme),
    themr(null, specificTheme),
    withRouter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedLocationMasterContainer = enhance(LocationMasterContainer);

export default ThemedLocationMasterContainer;
